import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Search,
  Download,
  Trash2,
  CheckCircle2,
  Circle,
  Mail,
  Phone,
  RefreshCw,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function downloadCsv(leads) {
  const headers = ["id", "name", "email", "phone", "goal", "message", "created_at", "contacted", "email_sent"];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = [headers.join(",")].concat(
    leads.map((l) =>
      headers.map((h) => escape(l[h])).join(",")
    )
  );
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const date = new Date().toISOString().slice(0, 10);
  a.download = `meno-leads-${date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminDashboard() {
  const { user, logout, authHeader, API } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | contacted | pending
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      else setRefreshing(true);
      try {
        const [leadsRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/leads`, { headers: authHeader() }),
          axios.get(`${API}/admin/stats`, { headers: authHeader() }),
        ]);
        setLeads(leadsRes.data || []);
        setStats(statsRes.data || null);
      } catch (err) {
        toast.error(formatApiError(err, "Could not load leads."));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [API, authHeader]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter === "contacted" && !l.contacted) return false;
      if (filter === "pending" && l.contacted) return false;
      if (!q) return true;
      return (
        l.name?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.phone?.toLowerCase().includes(q) ||
        l.goal?.toLowerCase().includes(q) ||
        l.message?.toLowerCase().includes(q)
      );
    });
  }, [leads, query, filter]);

  const toggleContacted = async (lead) => {
    const next = !lead.contacted;
    setLeads((prev) =>
      prev.map((l) => (l.id === lead.id ? { ...l, contacted: next } : l))
    );
    try {
      await axios.patch(
        `${API}/admin/leads/${lead.id}/contacted`,
        { contacted: next },
        { headers: authHeader() }
      );
      toast.success(next ? "Marked as contacted" : "Marked as pending");
    } catch (err) {
      setLeads((prev) =>
        prev.map((l) => (l.id === lead.id ? { ...l, contacted: !next } : l))
      );
      toast.error(formatApiError(err, "Update failed."));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: authHeader() });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  };

  const kpis = [
    { label: "Total leads", value: stats?.total ?? leads.length },
    { label: "Contacted", value: stats?.contacted ?? leads.filter((l) => l.contacted).length },
    { label: "Last 7 days", value: stats?.last_7_days ?? "—" },
    { label: "Emails sent", value: stats?.email_sent ?? leads.filter((l) => l.email_sent).length },
  ];

  return (
    <main
      data-testid="admin-dashboard-page"
      className="min-h-screen bg-[#0a0a0a] text-white"
    >
      <header className="border-b border-white/10 bg-[#0a0a0a]/90 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <div className="w-10 h-10 border border-white/20 flex items-center justify-center">
              <span className="font-display text-xl text-[#FF3B30] leading-none">M</span>
            </div>
            <div className="leading-tight">
              <div className="font-display text-lg tracking-wider">MEÑO COACHING</div>
              <div className="text-[10px] tracking-[0.35em] text-[#FF3B30] uppercase">
                Admin Dashboard
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-white/50 tracking-widest uppercase">
              {user?.email}
            </span>
            <button
              onClick={() => fetchData(true)}
              data-testid="admin-refresh"
              className="btn-secondary !py-2 !px-3 !text-xs"
              aria-label="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="btn-secondary !py-2 !px-4 !text-xs"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#FF3B30] mb-2">
              Leads
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none">
              Every prospect, one place.
            </h1>
          </div>
          <button
            onClick={() => downloadCsv(filtered)}
            disabled={!filtered.length}
            data-testid="export-csv"
            className="btn-primary disabled:opacity-50"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {kpis.map((k) => (
            <div
              key={k.label}
              data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-[#111] border border-white/10 p-6"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                {k.label}
              </div>
              <div className="font-display text-4xl mt-2">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
            />
            <input
              data-testid="search-leads"
              type="search"
              placeholder="Search name, email, phone, goal…"
              className="w-full bg-[#111] border border-white/10 pl-9 pr-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#FF3B30]"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="flex border border-white/10">
            {[
              { k: "all", label: "All" },
              { k: "pending", label: "Pending" },
              { k: "contacted", label: "Contacted" },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`filter-${f.k}`}
                className={`px-4 py-3 text-[11px] tracking-[0.2em] uppercase transition-colors ${
                  filter === f.k
                    ? "bg-[#FF3B30] text-white"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Leads table */}
        <div className="border border-white/10 bg-[#111]">
          {loading ? (
            <div
              data-testid="leads-loading"
              className="p-12 flex items-center justify-center text-white/60 text-sm tracking-widest uppercase"
            >
              <Loader2 size={16} className="animate-spin mr-3" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div
              data-testid="leads-empty"
              className="p-16 text-center"
            >
              <div className="font-display text-3xl uppercase mb-2">No leads yet</div>
              <p className="text-white/60 text-sm">
                {leads.length === 0
                  ? "When someone submits the form, they'll show up here."
                  : "Nothing matches your search or filter."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] tracking-[0.25em] uppercase text-white/50">
                    <th className="text-left px-4 py-4 w-10"></th>
                    <th className="text-left px-4 py-4">Prospect</th>
                    <th className="text-left px-4 py-4">Goal</th>
                    <th className="text-left px-4 py-4 hidden lg:table-cell">Message</th>
                    <th className="text-left px-4 py-4">Received</th>
                    <th className="text-right px-4 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead) => (
                    <tr
                      key={lead.id}
                      data-testid={`lead-row-${lead.id}`}
                      className={`border-b border-white/5 transition-colors ${
                        lead.contacted ? "opacity-60" : "hover:bg-white/[0.02]"
                      }`}
                    >
                      <td className="px-4 py-5 align-top">
                        <button
                          onClick={() => toggleContacted(lead)}
                          data-testid={`toggle-contacted-${lead.id}`}
                          className="text-white/60 hover:text-[#FF3B30]"
                          aria-label="Toggle contacted"
                        >
                          {lead.contacted ? (
                            <CheckCircle2 size={18} className="text-[#FF3B30]" />
                          ) : (
                            <Circle size={18} />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-5 align-top">
                        <div className="font-display text-lg tracking-wide uppercase">
                          {lead.name}
                        </div>
                        <div className="mt-1 space-y-1">
                          <a
                            href={`mailto:${lead.email}`}
                            className="flex items-center gap-2 text-white/80 hover:text-[#FF3B30] text-xs"
                          >
                            <Mail size={12} /> {lead.email}
                          </a>
                          <a
                            href={`tel:${lead.phone}`}
                            className="flex items-center gap-2 text-white/80 hover:text-[#FF3B30] text-xs"
                          >
                            <Phone size={12} /> {lead.phone}
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-5 align-top">
                        <span className="inline-block border border-white/15 px-2 py-1 text-[10px] tracking-[0.2em] uppercase">
                          {lead.goal}
                        </span>
                        {lead.email_sent && (
                          <div className="text-[10px] tracking-widest uppercase text-[#25D366] mt-2">
                            ✓ Email sent
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-5 align-top hidden lg:table-cell max-w-md">
                        <p className="text-white/70 text-xs leading-relaxed line-clamp-3">
                          {lead.message || "—"}
                        </p>
                      </td>
                      <td className="px-4 py-5 align-top text-white/60 text-xs whitespace-nowrap">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-4 py-5 align-top">
                        <div className="flex items-center justify-end gap-2">
                          <a
                            href={`mailto:${lead.email}?subject=Re%3A%20Your%20Meño%20Coaching%20inquiry`}
                            data-testid={`reply-${lead.id}`}
                            className="p-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-white/70"
                            title="Reply"
                          >
                            <ExternalLink size={14} />
                          </a>
                          <button
                            onClick={() => setDeleteTarget(lead)}
                            data-testid={`delete-${lead.id}`}
                            className="p-2 border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-white/70"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="text-xs text-white/40 text-center mt-10 tracking-widest uppercase">
          Pushing Limits — Admin
        </div>
      </section>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent
          data-testid="delete-dialog"
          className="bg-[#111] border border-white/10 text-white rounded-none"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase tracking-wide">
              Delete lead?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              This will permanently remove{" "}
              <span className="text-white">{deleteTarget?.name}</span> from your leads.
              This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel"
              className="bg-transparent border border-white/15 text-white hover:bg-white/5 rounded-none"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="delete-confirm"
              className="bg-[#FF3B30] text-white hover:bg-[#FF574D] rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
